var __importDefault = (this && this.__importDefault) || function (mod) {
  return (mod && mod.__esModule) ? mod : { "default": mod };
};
/**
 * YearView Component — Horizontal row-per-month calendar
 *
 * Columns represent week-aligned positions (repeating S M T W T F S).
 * Each month's dates are offset so day 1 lands in the correct DOW column.
 * Total columns = 37 (max offset 6 + 31 days covers every possible month).
 */
import React, { memo, useCallback, useMemo } from 'react';
import { isToday } from '../../utils/dateUtils';
import { styles } from './YearView.styles';
const DOW_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTH_ABBR = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
/** Total grid columns: 37 = max possible (6 offset + 31 days) */
const TOTAL_COLS = 37;
const daysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
const startDow = (year, month) => new Date(year, month, 1).getDay(); // 0=Sun
const isNonWorkDow = (dow, nonWorkDays) => {
    if (!nonWorkDays || nonWorkDays.length === 0)
        return false;
    const names = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return nonWorkDays.some(n => n.toLowerCase() === names[dow].toLowerCase());
};
/** Secondary cell background color by event type (matches getCEEventColor in ce-calendar) */
const EVENT_SECONDARY_COLORS = {
    project: '#99F6E4',
    nonproject: '#e8f5e9',
    publicholiday: '#B4A0FF',
    msvacation: '#B4A0FF',
    outlook: '#EBF3FC',
};
const getSecondaryCellColor = (events) => {
    // Priority: holiday > project > nonproject > outlook
    const categories = events.map(e => (e.eventTypeName || '').toLowerCase());
    if (categories.includes('publicholiday') || categories.includes('msvacation'))
        return EVENT_SECONDARY_COLORS.publicholiday;
    if (categories.includes('project'))
        return EVENT_SECONDARY_COLORS.project;
    if (categories.includes('nonproject'))
        return EVENT_SECONDARY_COLORS.nonproject;
    if (categories.includes('outlook'))
        return EVENT_SECONDARY_COLORS.outlook;
    return undefined;
};
/** Build lookup: unique key `year_month` → array of DayObjects indexed by day-of-month (0-based) */
const buildDayLookup = (monthsData) => {
    const map = new Map();
    monthsData.forEach(m => {
        const count = daysInMonth(m.year, m.month);
        const arr = new Array(count).fill(null);
        m.days.forEach(d => {
            if (!d.secondary && d.date.getMonth() === m.month) {
                arr[d.date.getDate() - 1] = d;
            }
        });
        map.set(`${m.year}_${m.month}`, arr);
    });
    return map;
};
export const YearView = memo(({ year, monthsData, eventMap, config, focusedDate, onDayClick, onMonthClick, }) => {
    const dayLookup = useMemo(() => buildDayLookup(monthsData), [monthsData]);
    const handleDayClick = useCallback((day) => {
        const events = eventMap[day.key] || [];
        onDayClick === null || onDayClick === void 0 ? void 0 : onDayClick(day, events);
    }, [eventMap, onDayClick]);
    /** Render one cell at grid column `col` for a given month */
    const renderCell = useCallback((m, col) => {
        const offset = startDow(m.year, m.month);
        const count = daysInMonth(m.year, m.month);
        const dayNum = col - offset; // 0-based day index
        // Columns before or after this month's days → empty cell (no non-work shading)
        if (dayNum < 0 || dayNum >= count) {
            return (React.createElement("td", { key: col, style: Object.assign(Object.assign({}, styles.dayCell), styles.dayCellEmpty), "aria-hidden": "true" }));
        }
        const days = dayLookup.get(`${m.year}_${m.month}`);
        const day = days ? days[dayNum] : null;
        if (!day) {
            return React.createElement("td", { key: col, style: Object.assign(Object.assign({}, styles.dayCell), styles.dayCellEmpty), "aria-hidden": "true" });
        }
        const events = eventMap[day.key] || [];
        const hasEvents = events.length > 0;
        const today = isToday(day.date);
        const nonWork = isNonWorkDow(day.date.getDay(), config.nonWorkDays);
        const hasHoliday = events.some(e => e.isPublicHoliday);
        // Secondary color for cell background based on event type
        const cellBg = hasEvents ? getSecondaryCellColor(events) : undefined;
        // Roving tabindex: only the currently focused date is tabbable so keyboard
        // users traverse the grid with arrow keys instead of hundreds of Tab presses.
        const isFocused = !!focusedDate &&
            day.date.getFullYear() === focusedDate.getFullYear() &&
            day.date.getMonth() === focusedDate.getMonth() &&
            day.date.getDate() === focusedDate.getDate();
        return (React.createElement("td", { key: col, "data-date": day.key, style: Object.assign(Object.assign(Object.assign(Object.assign(Object.assign({}, styles.dayCell), (nonWork ? styles.dayCellNonWork : {})), (today ? styles.dayCellToday : {})), (hasEvents ? styles.dayCellHasEvent : {})), (cellBg ? { backgroundColor: cellBg } : {})), onClick: () => handleDayClick(day), onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                handleDayClick(day);
            } }, role: "gridcell", tabIndex: isFocused ? 0 : -1, "aria-label": `${day.date.toLocaleDateString()}, ${events.length === 1 ? '1 event' : `${events.length} events`}${nonWork ? ', non-working day' : ''}` },
            day.label,
            hasEvents && (React.createElement("div", { style: styles.eventBarContainer }, events.slice(0, 3).map((evt, i) => (React.createElement("div", { key: i, style: Object.assign(Object.assign({}, styles.eventBlock), { backgroundColor: evt.bgcolor || '#0078d4' }), title: evt.name, "aria-hidden": "true" })))))));
    }, [dayLookup, eventMap, config.nonWorkDays, handleDayClick, focusedDate]);
    return (React.createElement("div", { style: styles.wrapper },
        React.createElement("table", { style: styles.table, role: "grid", "aria-label": `Yearly calendar ${year}` },
            React.createElement("thead", null,
                React.createElement("tr", null,
                    React.createElement("th", { style: styles.monthLabelHeader }),
                    Array.from({ length: TOTAL_COLS }, (_, i) => {
                        const dow = i % 7;
                        const nonWork = isNonWorkDow(dow, config.nonWorkDays);
                        return (React.createElement("th", { key: i, style: Object.assign(Object.assign({}, styles.dowHeaderCell), (nonWork ? styles.dowHeaderCellNonWork : {})), scope: "col", "aria-label": ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dow] }, DOW_LETTERS[dow]));
                    }))),
            React.createElement("tbody", null, monthsData.map(m => (React.createElement("tr", { key: `${m.year}-${m.month}` },
                React.createElement("td", { style: styles.monthLabel, onClick: () => onMonthClick === null || onMonthClick === void 0 ? void 0 : onMonthClick(m.month, m.year), onKeyDown: (e) => { if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onMonthClick === null || onMonthClick === void 0 ? void 0 : onMonthClick(m.month, m.year);
                    } }, role: "rowheader", tabIndex: -1, "aria-label": `${MONTH_ABBR[m.month]} ${m.year}` }, MONTH_ABBR[m.month]),
                Array.from({ length: TOTAL_COLS }, (_, i) => renderCell(m, i)))))))));
});
YearView.displayName = 'YearView';
export default YearView;
//# sourceMappingURL=YearView.js.map